import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import AppButton from "../button/AppButton";
import AppTextInput from "../inputs/AppTextInput";
import AppSelectInput from "../inputs/AppSelectInput";
import { CreateAdminCleanerRequest } from "../../models/RequestModels/CreateAdminCleanerRequest";
import { useAppSelector } from "../../store/hooks";

interface CreateCleanerModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: CreateAdminCleanerRequest) => void;
}

const initialState: CreateAdminCleanerRequest = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  password: "",
  role: "cleaner",
};

const CreateCleanerModal = ({
  open,
  onClose,
  onSubmit,
}: CreateCleanerModalProps) => {
  const { saving, fieldErrors } = useAppSelector(
    (state) => state.adminAccounts,
  );
  const [form, setForm] = useState<CreateAdminCleanerRequest>(initialState);

  const updateField = (key: keyof CreateAdminCleanerRequest, value: string) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleClose = () => {
    setForm(initialState);
    onClose();
  };

  const getError = (key: string) => fieldErrors[key]?.[0];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add team member</DialogTitle>

      <DialogContent>
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
            },
            gap: 2,
          }}
        >
          <Box sx={{ gridColumn: "1 / -1" }}>
            <AppSelectInput
              label="Role"
              value={form.role || "cleaner"}
              onChange={(value) => updateField("role", value)}
              options={[
                { label: "Manager", value: "manager" },
                { label: "Housekeeper", value: "cleaner" },
                { label: "Viewer", value: "viewer" },
              ]}
            />
          </Box>

          <AppTextInput
            label="First name"
            value={form.first_name}
            onChange={(value) => updateField("first_name", value)}
            error={getError("first_name")}
            required
          />

          <AppTextInput
            label="Last name"
            value={form.last_name || ""}
            onChange={(value) => updateField("last_name", value)}
            error={getError("last_name")}
          />

          <Box sx={{ gridColumn: "1 / -1" }}>
            <AppTextInput
              label="Email"
              type="email"
              value={form.email}
              onChange={(value) => updateField("email", value)}
              error={getError("email")}
              required
            />
          </Box>

          <Box sx={{ gridColumn: "1 / -1" }}>
            <AppTextInput
              label="Phone"
              value={form.phone || ""}
              onChange={(value) => updateField("phone", value)}
              error={getError("phone")}
            />
          </Box>

          <Box sx={{ gridColumn: "1 / -1" }}>
            <AppTextInput
              label="Temporary password"
              type="password"
              value={form.password}
              onChange={(value) => updateField("password", value)}
              error={getError("password")}
              required
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton variant="outlined" onClick={handleClose} disabled={saving}>
          Cancel
        </AppButton>
        <AppButton onClick={() => onSubmit(form)} loading={saving}>
          Add team member
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCleanerModal;
